import React from 'react';
import { Row, Col, Card, Button, Tag, Divider, Alert } from 'antd';
import { CheckCircleFilled, ExperimentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { PAYMENT_ENABLED } from '../../../constants/payment.js';
import { SUPPORT_HOTLINE_DISPLAY, SUPPORT_ZALO_URL } from '../../../constants/supportBotData.js';

export function PricingPlans({ plans = [] }) {
  const navigate = useNavigate();
  // Giai đoạn thử nghiệm miễn phí (chưa mở thanh toán): chỉ hiện gói Miễn phí, ẩn các gói hội viên trả phí
  const visiblePlans = PAYMENT_ENABLED ? plans : plans.filter((p) => p.code === 'MIEN_PHI');

  const formatPrice = (price) => {
    if (price === 0) return '0đ (Miễn phí)';
    return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(price);
  };

  const getPlanHighlights = (code) => {
    switch (code) {
      case 'MIEN_PHI':
        return [
          '7 Module mở nền tảng (S1, M1, N1, V1, C1, P1, D4)',
          'Toàn bộ tài liệu biểu mẫu đính kèm của 7 bài',
          'Nhận bản tin pháp lý & thông báo cập nhật',
          'Đổi lấy đăng ký SĐT/Zalo + Tỉnh thành',
        ];
      case 'KHOI_NGHIEP':
        return [
          'Trọn bộ Lộ trình 1 (12 module mở quầy/nhà thuốc)',
          'Bộ hồ sơ mẫu xin Giấy phép GPP chuẩn',
          '1 buổi hỏi đáp trực tiếp với chuyên gia',
          'Thời hạn học tập: 6 tháng',
        ];
      case 'TANG_TRUONG':
        return [
          'Trọn bộ Lộ trình 2 (15 module lõi tăng trưởng)',
          'Toàn bộ 50+ file công cụ Excel/Word/PDF',
          'Tham gia Zoom giải đáp & chữa bài hàng tháng',
          'Cụm bài AI Content - Ảnh Canva - Video CapCut',
          'Thời hạn học tập: 12 tháng',
        ];
      case 'HOI_VIEN_TOAN_DIEN':
        return [
          'Mở khóa toàn bộ 32 Module của cả 7 chuyên đề',
          'Cộng đồng riêng dành cho chủ nhà thuốc VIP',
          'Zoom huấn luyện chuyên sâu 2 buổi / tháng',
          'Cấp Chứng chỉ hoàn thành chương trình',
          'Cập nhật tài liệu pháp lý định kỳ 6 tháng/lần',
          'Thời hạn học tập: 12 tháng',
        ];
      case 'DOI_TAC':
        return [
          'Đặc quyền tương đương Gói Tăng Trưởng',
          'Dành riêng cho khách hàng B2B của Thế Thảo Pharma',
          'Tặng kèm 100% không phát sinh chi phí',
          'Hỗ trợ đào tạo trực tiếp cho nhân viên quầy',
        ];
      default:
        return [];
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ textAlign: 'center', marginBottom: 28 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: '#0f172a', marginBottom: 8 }}>
          Mô Hình Gói Học & Phân Quyền Hệ Thống
        </h2>
        <p style={{ fontSize: 14, color: '#64748b', maxWidth: 650, margin: '0 auto' }}>
          Được thiết kế linh hoạt từ trải nghiệm Miễn phí cho mọi dược sĩ đến các gói giải pháp chuyên sâu cho từng giai đoạn phát triển nhà thuốc.
        </p>
      </div>

      {!PAYMENT_ENABLED && (
        <Alert
          type="success"
          showIcon
          icon={<ExperimentOutlined />}
          style={{ maxWidth: 760, margin: '0 auto 24px', borderRadius: 12 }}
          message={<b>Nền tảng đang trong giai đoạn thử nghiệm miễn phí</b>}
          description={
            <>
              Trong thời gian thử nghiệm, anh/chị đăng ký tài khoản để học miễn phí các module nền tảng. Các gói hội
              viên sẽ được mở đăng ký khi kết thúc giai đoạn thử nghiệm. Cần tư vấn thêm, nhắn Zalo{' '}
              <a href={SUPPORT_ZALO_URL} target="_blank" rel="noopener noreferrer">
                <b>{SUPPORT_HOTLINE_DISPLAY}</b>
              </a>
              .
            </>
          }
        />
      )}

      <Row gutter={[20, 20]} justify="center">
        {visiblePlans.map((p) => {
          const isFeatured = p.code === 'HOI_VIEN_TOAN_DIEN';
          const isFree = p.price === 0;
          const highlights = getPlanHighlights(p.code);

          return (
            <Col xs={24} sm={12} lg={8} key={p.id || p.code}>
              <Card
                style={{
                  borderRadius: 14,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  border: isFeatured ? '2px solid #0284c7' : '1px solid #e2e8f0',
                  boxShadow: isFeatured ? '0 8px 24px rgba(2, 132, 199, 0.12)' : '0 2px 8px rgba(0,0,0,0.04)',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                styles={{ body: { padding: '24px 22px', display: 'flex', flexDirection: 'column', flex: 1 } }}
              >
                {isFeatured && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 14,
                      right: -32,
                      transform: 'rotate(45deg)',
                      background: '#0284c7',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 800,
                      padding: '4px 36px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    Gói Toàn Diện
                  </div>
                )}

                <div style={{ marginBottom: 12 }}>
                  <Tag color={isFree ? 'green' : isFeatured ? 'blue' : 'orange'} style={{ fontWeight: 700, borderRadius: 4 }}>
                    {p.name}
                  </Tag>
                </div>

                <div style={{ marginBottom: 14 }}>
                  <span style={{ fontSize: 26, fontWeight: 800, color: '#0f172a' }}>
                    {formatPrice(p.price)}
                  </span>
                  {p.price > 0 && (
                    <span style={{ fontSize: 13, color: '#64748b' }}> / {p.durationMonths} tháng</span>
                  )}
                </div>

                <p style={{ fontSize: 13, color: '#475569', minHeight: 40, lineHeight: 1.5, marginBottom: 16 }}>
                  {p.description}
                </p>

                <Divider style={{ margin: '8px 0 16px' }} />

                <div style={{ flex: 1, marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', marginBottom: 10 }}>
                    Quyền lợi bao gồm:
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {highlights.map((h, hIdx) => (
                      <div key={hIdx} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: 13, color: '#334155' }}>
                        <CheckCircleFilled style={{ color: '#10b981', marginTop: 3, flexShrink: 0 }} />
                        <span>{h}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <Button
                  type={isFeatured ? 'primary' : 'default'}
                  block
                  style={{
                    height: 42,
                    fontWeight: 700,
                    borderRadius: 8,
                    background: isFeatured ? '#0284c7' : undefined,
                    borderColor: isFeatured ? '#0284c7' : '#cbd5e1',
                  }}
                  onClick={() => navigate(isFree ? '/register' : `/checkout/${p.id}`)}
                >
                  {isFree ? 'Đăng Ký Học Miễn Phí' : 'Chọn Gói Này'}
                </Button>
              </Card>
            </Col>
          );
        })}
      </Row>
    </div>
  );
}
